import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  User, CreditCard, Shield, Camera, Check, X,
  AlertTriangle, Loader2, Eye, EyeOff,
  Lock, Zap, ArrowRight, ChevronRight, Globe, Paintbrush,
  Key, Plus, Trash2, Copy, Gift, Mail, ExternalLink,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { authFetch } from '@/lib/authFetch';
import { fileToDataUrl } from '@/lib/visualEdit';
import { messageCreditsForTier, integrationCreditsForTier } from '@/config/plans';
import { REFERRAL_DISCOUNT, commissionRange } from '@/config/earnings';
import SEOHead from '@/components/SEOHead';
import { ThemeChoice } from '@/components/ThemeToggle';
import CreditPacks from '@/components/checkout/CreditPacks';

const API_URL = import.meta.env.VITE_API_URL ?? '';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Display name and colour only. Credit allowances are NOT repeated here — they
// come from builderslab/shared/plans.json via @/config/plans, so this panel can
// never drift from the Pricing page again. ('agency' is sold as "Studio".)
const PLAN_LIMITS = {
  free:    { label: 'Free',    color: 'zinc'   },
  starter: { label: 'Starter', color: 'blue'   },
  pro:     { label: 'Pro',     color: 'orange' },
  agency:  { label: 'Studio',  color: 'purple' },
};

const PLAN_COLOR = {
  zinc:   'text-zinc-400 bg-zinc-800',
  blue:   'text-blue-400 bg-blue-500/15',
  orange: 'text-orange-400 bg-orange-500/15',
  purple: 'text-purple-400 bg-purple-500/15',
};

// The pricing page sells white-label branding, API access and a custom domain
// from Pro upward. These panels gated branding and API keys to Studio only,
// so Pro customers paid for features that were locked.
const PRO_TIERS = new Set(['pro', 'agency']);

const MAX_NAME = 80;
const CREDIT_PERIOD_DAYS = 30;

/** The branding JSON column, whichever way the driver handed it back. */
function readBranding(raw) {
  if (!raw) return {};
  if (typeof raw === 'string') { try { return JSON.parse(raw) || {}; } catch { return {}; } }
  return raw;
}

function InlineError({ message }) {
  if (!message) return null;
  return (
    <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} role="alert"
      className="flex items-center gap-1.5 text-red-400 text-xs mt-1.5">
      <X className="w-3.5 h-3.5 shrink-0" />{message}
    </motion.p>
  );
}

function InlineSuccess({ message }) {
  if (!message) return null;
  return (
    <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} role="status"
      className="flex items-center gap-1.5 text-emerald-400 text-xs mt-1.5">
      <Check className="w-3.5 h-3.5 shrink-0" />{message}
    </motion.p>
  );
}

/**
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.id
 * @param {string} [props.type]
 * @param {string} props.value
 * @param {(e: React.ChangeEvent<HTMLInputElement>) => void} [props.onChange]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.readOnly]
 * @param {string} [props.placeholder]
 * @param {string} [props.error]
 * @param {string} [props.hint]
 * @param {string} [props.autoComplete]
 * @param {React.HTMLAttributes<HTMLInputElement>['inputMode']} [props.inputMode]
 */
function InputField({ label, id, type = 'text', value, onChange, disabled, readOnly, placeholder, error = '', hint = '', autoComplete, inputMode }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-zinc-300 mb-2">{label}</label>
      <input
        id={id} type={type} value={value} onChange={onChange}
        disabled={disabled} readOnly={readOnly} placeholder={placeholder}
        autoComplete={autoComplete} inputMode={inputMode}
        aria-invalid={error ? 'true' : undefined}
        className={`w-full h-11 px-4 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-orange-500/50
          ${readOnly || disabled
            ? 'bg-zinc-900/50 border-zinc-800 text-zinc-500 cursor-not-allowed'
            : 'bg-zinc-900 border-zinc-700 text-white placeholder-zinc-600 hover:border-zinc-600 focus:border-orange-500'
          } ${error ? 'border-red-500 focus:ring-red-500/50' : ''}`}
      />
      {error ? <InlineError message={error} /> : hint ? <p className="text-zinc-600 text-xs mt-1.5">{hint}</p> : null}
    </div>
  );
}

function PlanGate({ requiredPlan, label }) {
  const navigate = useNavigate();
  return (
    <div className="rounded-2xl border bg-orange-500/10 border-orange-500/20 text-orange-400 p-8 text-center">
      <Lock className="w-10 h-10 mx-auto mb-4 opacity-70" />
      <h3 className="text-white font-bold text-lg mb-2">{label} is on {requiredPlan}</h3>
      <p className="text-zinc-400 text-sm mb-6 max-w-xs mx-auto">
        Upgrade your plan to unlock {label.toLowerCase()}.
      </p>
      <button
        onClick={() => navigate('/Pricing')}
        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-400 text-ink text-sm font-semibold transition-colors"
      >
        <Zap className="w-4 h-4" /> View Plans <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Referral code card ───────────────────────────────────────────────────────
// Every account's code, where people look for it: their profile. The full
// picture (earnings, payouts) is the dashboard's Earn section.

export function ReferralCodeCard({ code, onOpenEarn }) {
  const [copied, setCopied] = useState('');
  const shareUrl = code ? `${window.location.origin}/?ref=${encodeURIComponent(code)}` : '';

  const copy = (value, key) => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    }).catch(() => {});
  };

  return (
    <div className="rounded-2xl border border-orange-500/25 bg-orange-500/[0.06] p-5">
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-xl bg-orange-500/15 grid place-items-center shrink-0">
          <Gift className="w-5 h-5 text-orange-400" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-white font-semibold">Your referral code</p>
          <p className="text-zinc-400 text-sm mt-0.5 leading-relaxed">
            Friends get {REFERRAL_DISCOUNT} off any plan with it. You earn {commissionRange()} of what they pay.
          </p>
          {code ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="font-mono text-lg font-bold tracking-widest text-white bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-1.5">{code}</span>
              <button onClick={() => copy(code, 'code')}
                className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-xs font-semibold text-zinc-200 inline-flex items-center gap-1.5">
                {copied === 'code' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} Copy code
              </button>
              <button onClick={() => copy(shareUrl, 'link')}
                className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-xs font-semibold text-zinc-200 inline-flex items-center gap-1.5">
                {copied === 'link' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} Copy link
              </button>
            </div>
          ) : (
            <p className="mt-3 text-xs text-zinc-500">Loading your code…</p>
          )}
          {onOpenEarn && (
            <button onClick={onOpenEarn} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-orange-400 hover:text-orange-300">
              See your earnings <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Profile ─────────────────────────────────────────────────────────────

/** The form's starting values, from whatever has loaded so far. */
function profileForm(profile, user) {
  return {
    fullName: profile?.full_name || user?.user_metadata?.full_name || '',
    whatsapp: profile?.whatsapp_number || '',
  };
}

function EmailChange({ user }) {
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(/** @type {Record<string, string>} */ ({}));
  const [sentTo, setSentTo] = useState(user?.new_email || '');
  const hasPassword = user?.app_metadata?.has_password !== false;

  const submit = async (e) => {
    e?.preventDefault();
    setError({});
    setBusy(true);
    try {
      const res = await authFetch('/api/auth/change-email', {
        method: 'POST',
        body: JSON.stringify({ new_email: newEmail.trim(), password }),
      });
      setSentTo(res.pending_email);
      setOpen(false);
      setPassword('');
    } catch (err) {
      setError({ form: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (!hasPassword) {
    return (
      <p className="text-zinc-600 text-xs -mt-2">
        Your account signs in with {user?.app_metadata?.provider === 'apple' ? 'Apple' : 'Google'}, so its email follows that account.
        Set a password under Security to change it here.
      </p>
    );
  }

  return (
    <div className="-mt-2 space-y-3">
      {sentTo && !open && (
        <p className="flex items-start gap-1.5 text-xs text-amber-300">
          <Mail className="w-3.5 h-3.5 shrink-0 mt-px" />
          We sent a link to <span className="font-medium">{sentTo}</span>. Your email changes once you open it.
        </p>
      )}
      {!open ? (
        <button onClick={() => setOpen(true)} className="text-xs font-medium text-orange-400 hover:text-orange-300">
          Change email address
        </button>
      ) : (
        <form onSubmit={submit} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 space-y-3">
          <InputField label="New email address" id="new-email" type="email" value={newEmail}
            onChange={e => setNewEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
          <InputField label="Your password" id="email-password" type="password" value={password}
            onChange={e => setPassword(e.target.value)} autoComplete="current-password"
            hint="To confirm it is you." />
          <InlineError message={error.form} />
          <div className="flex gap-2">
            <button type="submit" disabled={busy || !newEmail.trim() || !password}
              className="h-9 px-4 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-ink text-xs font-semibold inline-flex items-center gap-1.5">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Send confirmation link
            </button>
            <button type="button" onClick={() => { setOpen(false); setError({}); }}
              className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs font-semibold text-zinc-300">
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export function ProfileTab({ user, profile, onProfileUpdate, onOpenEarn }) {
  const { refreshProfile } = useAuth();
  // The form is filled from the profile, which arrives after the first render.
  // It used to be read once into useState and never again, so the fields
  // stayed empty — and saving then wrote an empty WhatsApp number over the
  // real one. It now follows the profile until the user starts typing.
  const [form, setForm] = useState(() => profileForm(profile, user));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState(/** @type {Record<string, string>} */ ({}));
  const [success, setSuccess] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!dirty) setForm(profileForm(profile, user));
  }, [profile, user, dirty]);
  useEffect(() => { setAvatarUrl(profile?.avatar_url || null); }, [profile?.avatar_url]);

  const email = user?.email || profile?.email || '';
  const initial = (form.fullName || email || 'U')[0].toUpperCase();

  const update = (key) => (e) => {
    setForm(f => ({ ...f, [key]: e.target.value }));
    setDirty(true);
    setErrors(er => ({ ...er, [key]: '' }));
    setSuccess('');
  };

  const afterChange = async (nextProfile) => {
    if (nextProfile) onProfileUpdate?.(nextProfile);
    // The session's copy of the user (name, photo) and the shared profile —
    // what the navbar and the workspace read.
    try { await refreshProfile?.(); } catch { /* the page already shows the change */ }
  };

  const saveAvatar = async (url) => {
    const { profile: saved } = await authFetch('/api/me', { method: 'PATCH', body: JSON.stringify({ avatar_url: url }) });
    setAvatarUrl(url);
    await afterChange(saved);
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarError('');
    if (!/^image\//.test(file.type)) { setAvatarError('Choose an image file.'); return; }
    if (file.size > 10 * 1024 * 1024) { setAvatarError('That image is over 10 MB.'); return; }
    setAvatarBusy(true);
    try {
      // Shrunk in the browser first: a phone photo is several megabytes, and an
      // avatar is shown at 80px.
      const dataUrl = await fileToDataUrl(file, { maxDim: 512, quality: 0.85 });
      const { publicUrl } = await authFetch('/api/upload', {
        method: 'POST',
        body: JSON.stringify({ data_url: dataUrl, folder: 'avatars' }),
      });
      if (!publicUrl) throw new Error('The upload did not return an address.');
      await saveAvatar(publicUrl);
    } catch (err) {
      setAvatarError(err.message || 'Upload failed.');
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    setAvatarBusy(true);
    setAvatarError('');
    try { await saveAvatar(null); } catch (err) { setAvatarError(err.message); } finally { setAvatarBusy(false); }
  };

  const handleSave = async () => {
    const name = form.fullName.trim();
    /** @type {Record<string, string>} */
    const next = {};
    if (!name) next.fullName = 'Name cannot be empty.';
    else if (name.length < 2) next.fullName = 'Name must be at least 2 characters.';
    else if (name.length > MAX_NAME) next.fullName = `Keep your name under ${MAX_NAME} characters.`;
    const digits = form.whatsapp.replace(/\D/g, '');
    if (digits && (digits.length < 7 || digits.length > 15)) next.whatsapp = 'Use the full number with its country code, e.g. 2348012345678.';
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    setSuccess('');
    try {
      // Not authFetch: the server names the field that was wrong, and the
      // message belongs under that field rather than at the top of the form.
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/api/user/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ full_name: name, whatsapp_number: form.whatsapp.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const field = data.field === 'whatsapp_number' ? 'whatsapp' : data.field === 'full_name' ? 'fullName' : 'form';
        setErrors({ [field]: data.error || 'Failed to save.' });
        return;
      }
      setDirty(false);
      setForm(profileForm(data.profile, user));
      setSuccess('Profile saved.');
      await afterChange(data.profile);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setErrors({ form: err.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-5">
        <div className="relative group shrink-0">
          {avatarUrl
            ? <img src={avatarUrl} alt="Your profile photo" className="w-20 h-20 rounded-full object-cover ring-2 ring-zinc-700" />
            : <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center ring-2 ring-zinc-700 text-2xl font-bold text-white shadow-[0_0_30px_rgba(249,115,22,0.25)]">{initial}</div>
          }
          <button onClick={() => fileRef.current?.click()} disabled={avatarBusy} aria-label="Change photo"
            className="absolute inset-0 rounded-full bg-zinc-950/60 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity flex items-center justify-center">
            {avatarBusy ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} data-testid="avatar-input" />
        </div>
        <div className="min-w-0">
          <p className="text-white font-semibold truncate">{form.fullName || 'Your Name'}</p>
          <p className="text-zinc-500 text-sm truncate">{email}</p>
          <div className="flex items-center gap-3 mt-1">
            <button onClick={() => fileRef.current?.click()} disabled={avatarBusy} className="text-orange-400 hover:text-orange-300 text-xs transition-colors">
              {avatarBusy ? 'Uploading…' : avatarUrl ? 'Change photo' : 'Add photo'}
            </button>
            {avatarUrl && !avatarBusy && (
              <button onClick={removeAvatar} className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors">Remove</button>
            )}
          </div>
          <InlineError message={avatarError} />
        </div>
      </div>

      <div className="h-px bg-zinc-800" />

      <div className="space-y-5 max-w-md">
        <InputField label="Full Name" id="full-name" value={form.fullName}
          onChange={update('fullName')} placeholder="John Doe" error={errors.fullName} autoComplete="name" />
        <InputField label="Email Address" id="email" type="email" value={email} readOnly placeholder="your@email.com" />
        <EmailChange user={user} />
        <InputField label="WhatsApp number for orders" id="whatsapp-number" value={form.whatsapp}
          onChange={update('whatsapp')} placeholder="2348012345678" error={errors.whatsapp} inputMode="tel"
          hint="Used for the order button on sites you publish. Include your country code. Clear it to remove the button from new sites." />
      </div>

      <div>
        <button onClick={handleSave} disabled={saving || !dirty}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-ink text-sm font-semibold transition-all shadow-[0_4px_16px_rgba(249,115,22,0.3)]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {saving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
        </button>
        <InlineError message={errors.form} />
        <InlineSuccess message={success} />
      </div>

      <div className="h-px bg-zinc-800" />

      <ReferralCodeCard code={profile?.referral_code} onOpenEarn={onOpenEarn} />

      <div className="h-px bg-zinc-800" />

      {/* Applies the moment it is picked and is kept on this device, so it sits
          apart from the form above and has nothing to save. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-white font-semibold">Appearance</p>
          <p className="text-zinc-500 text-sm">Dark is the default. Your choice is kept on this device.</p>
        </div>
        <ThemeChoice />
      </div>
    </div>
  );
}

// ─── Tab: Billing ─────────────────────────────────────────────────────────────

export function BillingTab({ session, profile }) {
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const plan      = profile?.subscription_tier || 'free';
  const [status, setStatus] = useState(profile?.subscription_status || 'inactive');
  useEffect(() => { setStatus(profile?.subscription_status || 'inactive'); }, [profile?.subscription_status]);
  const isPaid    = plan !== 'free';
  const isCancelled = status === 'cancelled';
  const planMeta  = PLAN_LIMITS[plan] || PLAN_LIMITS.free;
  const planColorClasses = PLAN_COLOR[planMeta.color];

  const credits = profile?.credits;
  const limit = profile?.plan_limit ?? messageCreditsForTier(plan);
  const bought = Math.min(Math.max(profile?.extra_credits || 0, 0), Math.max(credits || 0, 0));
  const renews = profile?.last_credit_reset
    ? new Date(new Date(profile.last_credit_reset).getTime() + CREDIT_PERIOD_DAYS * 86400000)
    : null;

  // Flutterwave and NOWPayments have no hosted customer portal, so cancellation
  // happens here. The plan stays active until the paid period lapses.
  const handleCancel = async () => {
    setError(''); setNotice(''); setCancelling(true);
    try {
      const res  = await fetch(`${API_URL}/api/billing/cancel`, {
        method: 'POST', headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to cancel the subscription.');
      setStatus('cancelled');
      setConfirmCancel(false);
      setNotice('Your subscription will not renew. You keep your remaining credits until the end of the current period.');
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  // Credit rows are derived, never typed in. The old hardcoded "Generations /
  // day" row advertised 5/20/100/∞ — a daily model this product no longer sells.
  const creditRow = (label, forTier) => ({
    label,
    free:    forTier('free').toLocaleString(),
    starter: forTier('starter').toLocaleString(),
    pro:     forTier('pro').toLocaleString(),
    agency:  forTier('agency').toLocaleString(),
  });

  // Matches the pricing page, which is what customers bought.
  const PLAN_ROWS = [
    creditRow('Message credits / mo',     messageCreditsForTier),
    creditRow('Form submissions / mo',    integrationCreditsForTier),
    { label: 'Code export',        free: '✗', starter: '✓', pro: '✓', agency: '✓' },
    { label: 'Custom domains',     free: '✗', starter: '✗', pro: '1', agency: '3' },
    { label: 'White-label',        free: '✗', starter: '✗', pro: '✓', agency: '✓' },
    { label: 'API access',         free: '✗', starter: '✗', pro: '✓', agency: '✓' },
    { label: 'Buy extra credits',  free: '✗', starter: '✗', pro: '✓', agency: '✓' },
  ];

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Current Plan</p>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-bold ${planColorClasses}`}>{planMeta.label}</span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                isCancelled ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
                : status === 'active' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                : 'bg-zinc-800 border-zinc-700 text-zinc-500'}`}>
                {isCancelled ? 'Cancelled — not renewing' : status === 'active' ? 'Active' : 'Free tier'}
              </span>
            </div>
            <p className="text-zinc-500 text-sm mt-3">
              {messageCreditsForTier(plan).toLocaleString()} message credits per month
              {isPaid ? '' : ' · Code export locked'}
            </p>
          </div>
          {isPaid && !isCancelled ? (
            <button onClick={() => { setConfirmCancel(true); setError(''); setNotice(''); }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white text-sm font-semibold transition-all">
              <X className="w-4 h-4" />
              Cancel Subscription
            </button>
          ) : (
            <button onClick={() => navigate('/Pricing')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-400 text-ink text-sm font-bold transition-all shadow-[0_4px_16px_rgba(249,115,22,0.3)]">
              <Zap className="w-4 h-4" /> {isCancelled ? 'Resubscribe' : 'Upgrade Plan'} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Where the balance stands. */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl bg-zinc-950 border border-zinc-800 p-4">
            <p className="text-[10px] uppercase tracking-widest text-zinc-600 font-semibold">Credits left</p>
            <p className="text-2xl font-bold text-white tabular-nums mt-1">{typeof credits === 'number' ? credits : '…'}</p>
            <p className="text-[11px] text-zinc-600 mt-0.5">{limit} included each month</p>
          </div>
          <div className="rounded-xl bg-zinc-950 border border-zinc-800 p-4">
            <p className="text-[10px] uppercase tracking-widest text-zinc-600 font-semibold">Bought credits</p>
            <p className="text-2xl font-bold text-white tabular-nums mt-1">{bought}</p>
            <p className="text-[11px] text-zinc-600 mt-0.5">never expire</p>
          </div>
          <div className="rounded-xl bg-zinc-950 border border-zinc-800 p-4">
            <p className="text-[10px] uppercase tracking-widest text-zinc-600 font-semibold">{isCancelled ? 'Plan ends' : 'Credits refresh'}</p>
            <p className="text-base font-bold text-white mt-1.5">
              {renews ? renews.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
            </p>
            <p className="text-[11px] text-zinc-600 mt-0.5">every {CREDIT_PERIOD_DAYS} days</p>
          </div>
        </div>

        {confirmCancel && (
          <div className="mt-5 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
            <p className="text-white text-sm font-semibold mb-1">Cancel your {planMeta.label} plan?</p>
            <p className="text-zinc-400 text-sm mb-4">
              You keep the credits you have already paid for until the end of the current period.
              After that your account returns to the Free plan. You can resubscribe at any time.
            </p>
            <div className="flex flex-wrap gap-2.5">
              <button onClick={handleCancel} disabled={cancelling}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/90 hover:bg-red-500 text-white text-sm font-semibold transition-all disabled:opacity-60">
                {cancelling && <Loader2 className="w-4 h-4 animate-spin" />}
                Yes, cancel
              </button>
              <button onClick={() => setConfirmCancel(false)} disabled={cancelling}
                className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white text-sm font-semibold transition-all disabled:opacity-60">
                Keep my plan
              </button>
            </div>
          </div>
        )}

        {notice && (
          <p className="flex items-start gap-1.5 text-emerald-400 text-xs mt-3">
            <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" />{notice}
          </p>
        )}
        {error && <InlineError message={error} />}
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
        <CreditPacks />
      </div>

      <div>
        <h3 className="text-white font-semibold text-sm mb-4">Plan Comparison</h3>
        <div className="rounded-2xl border border-zinc-800 overflow-x-auto text-sm">
          <div className="min-w-[520px]">
            <div className="grid grid-cols-5 bg-zinc-900/80 px-4 py-3 text-xs text-zinc-500 font-semibold uppercase tracking-wider">
              <span className="col-span-1">Feature</span>
              {['free', 'starter', 'pro', 'agency'].map(tier => (
                <span key={tier} className={`text-center ${plan === tier ? 'text-orange-400' : ''}`}>
                  {PLAN_LIMITS[tier].label}
                </span>
              ))}
            </div>
            {PLAN_ROWS.map((row, i) => (
              <div key={row.label} className={`grid grid-cols-5 px-4 py-3 ${i % 2 === 0 ? 'bg-zinc-950' : 'bg-zinc-900/40'}`}>
                <span className="text-zinc-300 col-span-1">{row.label}</span>
                {['free', 'starter', 'pro', 'agency'].map(p => (
                  <span key={p} className={`text-center font-medium ${plan === p ? 'text-white' : 'text-zinc-600'} ${row[p] === '✓' ? 'text-emerald-400' : ''} ${row[p] === '✗' ? 'text-zinc-700' : ''}`}>
                    {row[p]}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Security ────────────────────────────────────────────────────────────

function PasswordInput({ id, label, value, onChange, visible, onToggle, autoComplete, error }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-zinc-300 mb-2">{label}</label>
      <div className="relative">
        <input id={id} type={visible ? 'text' : 'password'} value={value} onChange={onChange} autoComplete={autoComplete}
          aria-invalid={error ? 'true' : undefined}
          className={`w-full h-11 px-4 pr-11 rounded-xl border bg-zinc-900 text-white text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/30 transition-colors placeholder-zinc-600 ${error ? 'border-red-500' : 'border-zinc-700'}`}
          placeholder="••••••••" />
        <button type="button" onClick={onToggle} aria-label={visible ? 'Hide password' : 'Show password'}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      <InlineError message={error} />
    </div>
  );
}

const GOOGLE_ICON = (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const APPLE_ICON = (
  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M16.37 12.6c-.03-2.6 2.13-3.86 2.22-3.92-1.21-1.77-3.1-2.01-3.77-2.04-1.6-.16-3.13.95-3.94.95-.82 0-2.07-.93-3.4-.9-1.75.03-3.36 1.02-4.26 2.58-1.82 3.15-.47 7.81 1.3 10.37.87 1.25 1.9 2.65 3.25 2.6 1.31-.05 1.8-.84 3.38-.84 1.57 0 2.02.84 3.4.81 1.4-.02 2.29-1.27 3.15-2.53.99-1.45 1.4-2.86 1.42-2.93-.03-.01-2.72-1.04-2.75-4.15zM13.78 4.95c.72-.87 1.2-2.08 1.07-3.29-1.03.04-2.28.69-3.02 1.56-.66.77-1.24 2-1.09 3.19 1.15.09 2.32-.58 3.04-1.46z"/>
  </svg>
);

export function SecurityTab({ user }) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw,    setNewPw]    = useState('');
  const [confirmPw,setConfirmPw]= useState('');
  const [showPw,   setShowPw]   = useState({ current: false, new: false, confirm: false });
  const [pwErrors, setPwErrors] = useState(/** @type {Record<string, string>} */ ({}));
  const [pwSuccess,setPwSuccess]= useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm,   setDeleteConfirm]   = useState('');
  const [deletePassword,  setDeletePassword]  = useState('');
  const [forfeit,         setForfeit]         = useState(null);   // { amount } when earnings are owed
  const [deleting,        setDeleting]        = useState(false);
  const [deleteError,     setDeleteError]     = useState('');

  // What the account can actually sign in with. This read
  // `app_metadata.providers`, which the server never sent, so a Google account
  // was shown as not linked to Google and every account — Google-only ones
  // too — was shown an email-and-password login it may not have had.
  const provider = user?.app_metadata?.provider || 'email';
  const hasPassword = user?.app_metadata?.has_password ?? provider === 'email';

  const handleChangePassword = async (e) => {
    e?.preventDefault();
    setPwErrors({}); setPwSuccess('');
    /** @type {Record<string, string>} */
    const next = {};
    if (hasPassword && !currentPw) next.current = 'Enter your current password.';
    if (!newPw) next.new = 'New password is required.';
    else if (newPw.length < 8) next.new = 'Password must be at least 8 characters.';
    else if (newPw !== confirmPw) next.confirm = 'Passwords do not match.';
    if (Object.keys(next).length) { setPwErrors(next); return; }

    setSavingPw(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPw, current_password: currentPw || undefined });
      if (error) {
        setPwErrors({ [error.field === 'current_password' ? 'current' : error.field === 'password' ? 'new' : 'form']: error.message });
        return;
      }
      setPwSuccess(hasPassword ? 'Password updated.' : 'Password set — you can now also sign in with your email.');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => setPwSuccess(''), 5000);
    } catch (err) {
      setPwErrors({ form: err.message });
    } finally {
      setSavingPw(false);
    }
  };

  const closeDelete = () => {
    if (deleting) return;
    setShowDeleteModal(false); setDeleteConfirm(''); setDeletePassword(''); setDeleteError(''); setForfeit(null);
  };

  // Deletes the account for real. It used to sign the user out and nothing
  // else, while promising their data had been erased.
  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') { setDeleteError('Type DELETE to confirm.'); return; }
    if (hasPassword && !deletePassword) { setDeleteError('Enter your password.'); return; }
    setDeleting(true);
    setDeleteError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/api/auth/delete-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({
          ...(hasPassword ? { password: deletePassword } : { confirm: 'DELETE' }),
          ...(forfeit ? { forfeit_earnings: true } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && data.code === 'UNPAID_EARNINGS') {
        setForfeit({ amount: data.amount_usd });
        setDeleteError(data.error);
        setDeleting(false);
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Could not delete your account.');
      await signOut();
      navigate('/', { replace: true });
    } catch (err) {
      setDeleteError(err.message);
      setDeleting(false);
    }
  };

  const accounts = [
    { key: 'google', label: 'Google', icon: GOOGLE_ICON, connected: provider === 'google', iconBg: 'bg-snow' },
    { key: 'apple', label: 'Apple', icon: APPLE_ICON, connected: provider === 'apple', iconBg: 'bg-zinc-800' },
  ];

  return (
    <div className="space-y-10">
      <form onSubmit={handleChangePassword}>
        <h3 className="text-white font-semibold mb-1">{hasPassword ? 'Change Password' : 'Set a Password'}</h3>
        <p className="text-zinc-500 text-sm mb-6">
          {hasPassword
            ? 'Enter your current password, then choose a new one of at least 8 characters.'
            : `You sign in with ${provider === 'apple' ? 'Apple' : 'Google'}. Set a password to also sign in with your email.`}
        </p>
        <div className="space-y-4 max-w-md">
          {hasPassword && (
            <PasswordInput id="current-pw" label="Current Password" value={currentPw} autoComplete="current-password"
              onChange={e => { setCurrentPw(e.target.value); setPwErrors({}); }} error={pwErrors.current}
              visible={showPw.current} onToggle={() => setShowPw(p => ({ ...p, current: !p.current }))} />
          )}
          <PasswordInput id="new-pw" label="New Password" value={newPw} autoComplete="new-password"
            onChange={e => { setNewPw(e.target.value); setPwErrors({}); }} error={pwErrors.new}
            visible={showPw.new} onToggle={() => setShowPw(p => ({ ...p, new: !p.new }))} />
          <PasswordInput id="confirm-pw" label="Confirm New Password" value={confirmPw} autoComplete="new-password"
            onChange={e => { setConfirmPw(e.target.value); setPwErrors({}); }} error={pwErrors.confirm}
            visible={showPw.confirm} onToggle={() => setShowPw(p => ({ ...p, confirm: !p.confirm }))} />
          <InlineError message={pwErrors.form} />
          <InlineSuccess message={pwSuccess} />
        </div>
        <button type="submit" disabled={savingPw}
          className="mt-5 flex items-center gap-2 px-6 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-ink text-sm font-semibold transition-all">
          {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
          {savingPw ? 'Updating…' : hasPassword ? 'Update Password' : 'Set Password'}
        </button>
      </form>

      <div className="h-px bg-zinc-800" />

      <div>
        <h3 className="text-white font-semibold mb-1">Sign-in Methods</h3>
        <p className="text-zinc-500 text-sm mb-5">How you can get into your BuildersLab account.</p>
        <div className="space-y-3 max-w-md">
          {accounts.map(({ key, label, icon, connected, iconBg }) => (
            <div key={key} className="flex items-center justify-between p-4 rounded-xl border border-zinc-800 bg-zinc-900">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconBg}`}>{icon}</div>
                <div>
                  <p className="text-white text-sm font-medium">{label}</p>
                  <p className="text-zinc-500 text-xs">{connected ? `Signs in with ${label}` : 'Not used by this account'}</p>
                </div>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${connected ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-zinc-800 border-zinc-700 text-zinc-500'}`}>
                {connected ? 'Connected' : 'Not linked'}
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between p-4 rounded-xl border border-zinc-800 bg-zinc-900">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-zinc-800 flex items-center justify-center"><Lock className="w-4 h-4 text-zinc-400" /></div>
              <div>
                <p className="text-white text-sm font-medium">Email & Password</p>
                <p className="text-zinc-500 text-xs">{user?.email}</p>
              </div>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${hasPassword ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-zinc-800 border-zinc-700 text-zinc-500'}`}>
              {hasPassword ? 'Active' : 'No password set'}
            </span>
          </div>
        </div>
      </div>

      <div className="h-px bg-zinc-800" />

      <div>
        <h3 className="text-red-400 font-semibold mb-1 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Danger Zone</h3>
        <p className="text-zinc-500 text-sm mb-5">Permanently delete your account and all associated data. This cannot be undone.</p>
        <button onClick={() => setShowDeleteModal(true)}
          className="px-5 py-2.5 rounded-xl border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 text-sm font-semibold transition-all">
          Delete Account
        </button>
      </div>

      <AnimatePresence>
        {showDeleteModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-zinc-950/90 backdrop-blur flex items-center justify-center p-4"
            onClick={closeDelete}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
              onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="delete-title"
              className="bg-zinc-900 border border-red-500/20 rounded-2xl p-8 max-w-md w-full shadow-2xl">
              <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-5">
                <AlertTriangle className="w-7 h-7 text-red-400" />
              </div>
              <h2 id="delete-title" className="text-xl font-extrabold text-white text-center mb-2">Delete Your Account?</h2>
              <p className="text-zinc-400 text-sm text-center mb-6">
                Your websites, published links, credits, listings and billing data will be permanently erased.
                Type <span className="font-bold text-red-400">DELETE</span> to confirm.
              </p>
              <input type="text" value={deleteConfirm} onChange={e => { setDeleteConfirm(e.target.value); setDeleteError(''); }}
                placeholder="Type DELETE" aria-label="Type DELETE to confirm"
                className="w-full h-11 px-4 rounded-xl border border-zinc-700 bg-zinc-950 text-white text-sm mb-3 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/30 transition-all placeholder-zinc-600" />
              {hasPassword && (
                <input type="password" value={deletePassword} onChange={e => { setDeletePassword(e.target.value); setDeleteError(''); }}
                  placeholder="Your password" aria-label="Your password" autoComplete="current-password"
                  className="w-full h-11 px-4 rounded-xl border border-zinc-700 bg-zinc-950 text-white text-sm mb-3 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/30 transition-all placeholder-zinc-600" />
              )}
              {deleteError && <InlineError message={deleteError} />}
              {forfeit && (
                <p className="mt-2 text-xs text-amber-300">Press Delete Account again to give up ${Number(forfeit.amount).toFixed(2)} and delete anyway.</p>
              )}
              <div className="flex gap-3 mt-4">
                <button onClick={closeDelete}
                  className="flex-1 h-11 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-semibold transition-colors">Cancel</button>
                <button onClick={handleDeleteAccount} disabled={deleting || deleteConfirm !== 'DELETE' || (hasPassword && !deletePassword)}
                  className="flex-1 h-11 rounded-xl bg-red-500 hover:bg-red-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-all flex items-center justify-center gap-2">
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {deleting ? 'Deleting…' : 'Delete Account'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Tab: Custom Domains ──────────────────────────────────────────────────────
// Custom domains are connected per site, in the Share panel. This said
// "Coming Soon" with a waitlist box while that panel already connected them,
// so this lists what is connected and says where to do it.

export function CustomDomainsTab({ profile }) {
  const plan  = profile?.subscription_tier || 'free';
  const isPro = PRO_TIERS.has(plan);
  const navigate = useNavigate();
  const [sites, setSites] = useState(null);

  useEffect(() => {
    if (!isPro) return undefined;
    let cancelled = false;
    authFetch('/api/websites')
      .then(({ websites }) => { if (!cancelled) setSites(Array.isArray(websites) ? websites : []); })
      .catch(() => { if (!cancelled) setSites([]); });
    return () => { cancelled = true; };
  }, [isPro]);

  if (!isPro) return <PlanGate requiredPlan="Pro" label="Custom domains" />;

  const connected = (sites || []).filter(s => s.custom_domain);
  const allowance = plan === 'agency' ? 3 : 1;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-5">
        <p className="text-white font-semibold">How to connect a domain</p>
        <ol className="mt-3 space-y-2 text-sm text-zinc-400 list-decimal pl-5">
          <li>Open the site in your workspace and press <span className="text-white font-medium">Share</span>.</li>
          <li>Under <span className="text-white font-medium">Use your own domain</span>, enter it (for example <span className="font-mono text-zinc-300">yourbusiness.com</span>) and save.</li>
          <li>Add the DNS record it shows you at your registrar. It goes live once DNS updates and the certificate is issued.</li>
        </ol>
        <p className="mt-3 text-xs text-zinc-600">Your plan includes {allowance} custom domain{allowance === 1 ? '' : 's'}.</p>
      </div>

      <div>
        <h3 className="text-white font-semibold mb-3">Connected domains</h3>
        {sites === null ? (
          <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : connected.length === 0 ? (
          <div className="flex flex-col items-center py-10 text-center border border-dashed border-zinc-800 rounded-2xl">
            <Globe className="w-8 h-8 text-zinc-700 mb-3" />
            <p className="text-zinc-500 text-sm">No domains connected yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {connected.map(site => (
              <div key={site.id} className="flex items-center justify-between gap-3 p-4 rounded-xl border border-zinc-800 bg-zinc-950">
                <div className="min-w-0">
                  <p className="text-white text-sm font-mono truncate">{site.custom_domain}</p>
                  <p className="text-zinc-500 text-xs truncate">{site.title || 'Untitled site'}</p>
                </div>
                <button onClick={() => navigate(`/build/${site.id}`, { state: { website: site } })}
                  className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-orange-400 hover:text-orange-300">
                  Open site <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Branding ────────────────────────────────────────────────────────────

export function BrandingTab({ profile, onProfileUpdate }) {
  const plan     = profile?.subscription_tier || 'free';
  const allowed  = PRO_TIERS.has(plan);
  const branding = readBranding(profile?.branding);

  const [accentColor,     setAccentColor]     = useState(branding.accent_color || '#f97316');
  const [logoUrl,         setLogoUrl]         = useState(branding.logo_url || null);
  const [uploadingLogo,   setUploadingLogo]   = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [success,         setSuccess]         = useState('');
  const [error,           setError]           = useState('');
  const [dirty,           setDirty]           = useState(false);
  const logoRef = useRef(null);

  // Same reason as the profile form: the profile arrives after the first
  // render, and a form read once would show (and save) the defaults.
  useEffect(() => {
    if (dirty) return;
    const b = readBranding(profile?.branding);
    setAccentColor(b.accent_color || '#f97316');
    setLogoUrl(b.logo_url || null);
  }, [profile?.branding, dirty]);

  if (!allowed) return <PlanGate requiredPlan="Pro" label="White-label branding" />;

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('Logo must be under 10 MB.'); return; }
    setUploadingLogo(true); setError('');
    try {
      const dataUrl = /svg/i.test(file.type)
        ? await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(new Error('That file could not be read.'));
          reader.readAsDataURL(file);
        })
        : await fileToDataUrl(file, { maxDim: 800, quality: 0.9 });
      const { publicUrl } = await authFetch('/api/upload', { method: 'POST', body: JSON.stringify({ data_url: dataUrl, folder: 'logos' }) });
      setLogoUrl(publicUrl);
      setDirty(true);
    } catch (err) {
      setError('Logo upload failed: ' + err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      // `remove_watermark` is deliberately not written: the badge is decided by
      // the plan alone, and the server strips this key if it is sent.
      const newBranding = { ...readBranding(profile?.branding), accent_color: accentColor, logo_url: logoUrl };
      const { profile: saved } = await authFetch('/api/me', { method: 'PATCH', body: JSON.stringify({ branding: newBranding }) });
      setSuccess('Branding settings saved.');
      setDirty(false);
      setTimeout(() => setSuccess(''), 4000);
      onProfileUpdate?.(saved || { ...profile, branding: newBranding });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Watermark — status, not a control. Removal follows the plan, so a
          toggle here would be a switch with nothing behind it. */}
      <div>
        <h3 className="text-white font-semibold mb-1">BuildersLab Badge</h3>
        <p className="text-zinc-500 text-sm mb-4">
          The "Built with BuildersLab" badge appears on sites built on the free plan. Every paid plan removes it automatically.
        </p>
        <div className="flex items-center gap-3 p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 max-w-md">
          <Check className="w-4 h-4 text-emerald-400 shrink-0" />
          <div>
            <p className="text-white text-sm font-medium">Your sites are unbranded</p>
            <p className="text-zinc-500 text-xs mt-0.5">Included with your plan — nothing to configure.</p>
          </div>
        </div>
      </div>

      <div className="h-px bg-zinc-800" />

      <div>
        <h3 className="text-white font-semibold mb-1">Your Logo</h3>
        <p className="text-zinc-500 text-sm mb-4">Upload your logo to replace the BuildersLab branding.</p>
        <div className="flex items-center gap-4">
          {logoUrl ? (
            <img src={logoUrl} alt="Your logo" className="w-16 h-16 rounded-xl object-contain bg-zinc-800 p-1 ring-2 ring-zinc-700" />
          ) : (
            <div className="w-16 h-16 rounded-xl bg-zinc-800 border-2 border-dashed border-zinc-700 flex items-center justify-center">
              <Paintbrush className="w-6 h-6 text-zinc-600" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <button onClick={() => logoRef.current?.click()} disabled={uploadingLogo}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-medium border border-zinc-700 transition-colors disabled:opacity-60">
                {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                {uploadingLogo ? 'Uploading…' : 'Upload Logo'}
              </button>
              {logoUrl && (
                <button onClick={() => { setLogoUrl(null); setDirty(true); }} className="text-xs text-zinc-500 hover:text-zinc-300">Remove</button>
              )}
            </div>
            <p className="text-zinc-600 text-xs mt-1.5">PNG, JPEG or SVG</p>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
          </div>
        </div>
      </div>

      <div className="h-px bg-zinc-800" />

      <div>
        <h3 className="text-white font-semibold mb-1">Brand Color</h3>
        <p className="text-zinc-500 text-sm mb-4">Your primary accent color.</p>
        <div className="flex items-center gap-3">
          <input type="color" value={accentColor} onChange={e => { setAccentColor(e.target.value); setDirty(true); }} aria-label="Brand colour"
            className="w-12 h-12 rounded-xl cursor-pointer bg-transparent border-2 border-zinc-700 p-0.5" />
          <code className="text-zinc-300 text-sm font-mono">{accentColor}</code>
        </div>
      </div>

      {error   && <InlineError   message={error}   />}
      {success && <InlineSuccess message={success} />}

      <button onClick={handleSave} disabled={saving}
        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-ink text-sm font-semibold transition-all shadow-[0_4px_16px_rgba(249,115,22,0.3)]">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        {saving ? 'Saving…' : 'Save Branding'}
      </button>
    </div>
  );
}

// ─── Tab: API Keys ────────────────────────────────────────────────────────────

export function ApiKeysTab({ session, profile }) {
  const plan     = profile?.subscription_tier || 'free';
  const allowed  = PRO_TIERS.has(plan);

  const [keys,       setKeys]       = useState([]);
  const [loadingKeys,setLoadingKeys]= useState(true);
  const [newName,    setNewName]    = useState('');
  const [creating,   setCreating]   = useState(false);
  const [rawKey,     setRawKey]     = useState(null);
  const [createError,setCreateError]= useState('');
  const [copied,     setCopied]     = useState(false);

  const fetchKeys = useCallback(async () => {
    if (!allowed) return;
    setLoadingKeys(true);
    try {
      const res  = await fetch(`${API_URL}/api/keys`, { headers: { Authorization: `Bearer ${session?.access_token}` } });
      const data = await res.json();
      if (res.ok) setKeys(data.keys || []);
    } catch { /* the list just stays empty */ } finally {
      setLoadingKeys(false);
    }
  }, [allowed, session?.access_token]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true); setCreateError('');
    try {
      const res  = await fetch(`${API_URL}/api/keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ name: newName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create key.');
      setRawKey(data.key.raw_key);
      setNewName('');
      await fetchKeys();
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this API key? This cannot be undone.')) return;
    await fetch(`${API_URL}/api/keys/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${session?.access_token}` } });
    setKeys(prev => prev.filter(k => k.id !== id));
    if (rawKey) setRawKey(null);
  };

  if (!allowed) return <PlanGate requiredPlan="Pro" label="API access" />;

  return (
    <div className="space-y-6">
      <AnimatePresence>
        {rawKey && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 space-y-3">
            <p className="text-emerald-400 text-sm font-semibold flex items-center gap-2">
              <Check className="w-4 h-4" /> API key created — copy it now. It won't be shown again.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs font-mono bg-zinc-950 text-zinc-200 px-3 py-2.5 rounded-lg break-all border border-zinc-800">{rawKey}</code>
              <button onClick={() => { navigator.clipboard.writeText(rawKey); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                className="shrink-0 p-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors" title="Copy" aria-label="Copy API key">
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <button onClick={() => setRawKey(null)} className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors">Dismiss</button>
          </motion.div>
        )}
      </AnimatePresence>

      <div>
        <h3 className="text-white font-semibold mb-3">Create New API Key</h3>
        <div className="flex gap-3 max-w-md">
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
            placeholder="Key name (e.g. Production)"
            aria-label="Key name"
            className="flex-1 h-11 px-4 rounded-xl border border-zinc-700 bg-zinc-900 text-white text-sm outline-none focus:border-orange-500 placeholder-zinc-600"
          />
          <button onClick={handleCreate} disabled={creating || !newName.trim()}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-ink text-sm font-semibold transition-all whitespace-nowrap">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create
          </button>
        </div>
        {createError && <InlineError message={createError} />}
      </div>

      {loadingKeys ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-zinc-500 animate-spin" /></div>
      ) : keys.length === 0 ? (
        <div className="flex flex-col items-center py-10 text-center border border-dashed border-zinc-800 rounded-2xl">
          <Key className="w-8 h-8 text-zinc-700 mb-3" />
          <p className="text-zinc-500 text-sm">No API keys yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-zinc-500 text-xs">{keys.length} of 10 keys used</p>
          {keys.map(key => (
            <div key={key.id} className="flex items-center justify-between p-4 rounded-xl border border-zinc-800 bg-zinc-950">
              <div>
                <p className="text-white text-sm font-medium">{key.name}</p>
                <p className="text-zinc-500 text-xs mt-0.5">
                  Created {new Date(key.created_at).toLocaleDateString('en-GB')}
                  {key.last_used_at && ` · Last used ${new Date(key.last_used_at).toLocaleDateString('en-GB')}`}
                </p>
              </div>
              <button onClick={() => handleRevoke(key.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 text-xs font-semibold transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Revoke
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800 text-xs text-zinc-500 space-y-1">
        <p className="text-zinc-300 font-medium text-sm mb-2">Using the API</p>
        <p>Include your key in the <code className="text-zinc-400 font-mono">Authorization</code> header:</p>
        <code className="block font-mono text-zinc-400 bg-zinc-950 px-3 py-2 rounded-lg mt-2 border border-zinc-800">
          Authorization: Bearer bl_your_key_here
        </code>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const TABS = [
  { id: 'profile',  label: 'Profile',        icon: User       },
  { id: 'billing',  label: 'Billing & credits', icon: CreditCard },
  { id: 'security', label: 'Security',        icon: Shield     },
  { id: 'domains',  label: 'Custom Domains',  icon: Globe      },
  { id: 'branding', label: 'Branding',        icon: Paintbrush },
  { id: 'api',      label: 'API Keys',        icon: Key        },
];

export default function Settings() {
  const { user, session, profile: sharedProfile, setProfile: setSharedProfile } = useAuth();
  const navigate          = useNavigate();
  const { search }        = useLocation();
  const requested = new URLSearchParams(search).get('tab');
  const [activeTab, setActiveTab] = useState(TABS.some(t => t.id === requested) ? requested : 'profile');
  const [profile,   setProfile]   = useState(sharedProfile || null);

  useEffect(() => {
    if (!user && !session) navigate('/', { replace: true });
  }, [user, session, navigate]);

  useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('*').eq('id', user.id).single().then(({ data }) => { if (data) setProfile(data); });
  }, [user]);

  const onProfileUpdate = (next) => {
    setProfile(next);
    setSharedProfile?.(next);
  };

  if (!user) return null;

  const active = TABS.find(t => t.id === activeTab) || TABS[0];

  return (
    <div className="min-h-screen bg-zinc-950 text-white pt-20">
      <SEOHead
        title="Account Settings"
        description="Manage your BuildersLab profile, billing, and security preferences."
        noIndex={true}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <h1 className="text-3xl font-extrabold text-white">Account Settings</h1>
          <p className="text-zinc-500 text-sm mt-1">Manage your profile, billing, and security preferences.</p>
        </motion.div>

        <div className="flex gap-8 flex-col sm:flex-row">
          <div className="sm:w-52 shrink-0">
            <nav className="flex sm:flex-col gap-1 overflow-x-auto sm:overflow-visible -mx-4 px-4 sm:mx-0 sm:px-0">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => setActiveTab(id)}
                  aria-current={activeTab === id ? 'page' : undefined}
                  className={`shrink-0 sm:w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all text-left whitespace-nowrap ${
                    activeTab === id ? 'bg-zinc-800 text-white shadow-inner' : 'text-zinc-500 hover:text-white hover:bg-zinc-900'
                  }`}>
                  <Icon className={`w-4 h-4 shrink-0 ${activeTab === id ? 'text-orange-400' : ''}`} />
                  {label}
                  {activeTab === id && <ChevronRight className="hidden sm:block w-3.5 h-3.5 ml-auto text-zinc-600" />}
                </button>
              ))}
            </nav>
          </div>

          <div className="flex-1 min-w-0">
            <motion.div key={activeTab} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2 }}
              className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5 sm:p-7">
              <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <active.icon className="w-5 h-5 text-orange-400" />{active.label}
              </h2>

              {activeTab === 'profile'  && <ProfileTab user={user} profile={profile} onProfileUpdate={onProfileUpdate} onOpenEarn={() => navigate('/dashboard?section=earn')} />}
              {activeTab === 'billing'  && <BillingTab session={session} profile={profile} />}
              {activeTab === 'security' && <SecurityTab user={user} />}
              {activeTab === 'domains'  && <CustomDomainsTab profile={profile} />}
              {activeTab === 'branding' && <BrandingTab profile={profile} onProfileUpdate={onProfileUpdate} />}
              {activeTab === 'api'      && <ApiKeysTab session={session} profile={profile} />}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
